import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useTracking } from 'react-tracking';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import FormatQuote from '@material-ui/icons/FormatQuote';

import ContextMenu from 'components/shared/ContextMenu';
import Stack from 'components/shared/Stack';

import { SmsCharacterLimit, SystemPresenceTimeout } from 'utils/constants';
import { copyToClipboard } from 'utils/functions';

import { RootState } from 'state/store';
import { setNotification } from 'state/app.state';
import { sendMessageToLead, setSystemPresenceEngagement } from 'state/concierge.state';
import {
  apiCreateNote,
  clearAiPromptResponse,
  selectLeadEmailCapable,
  selectLeadSmsCapable,
} from 'state/lead.state';
import { setIsOnScript } from 'state/script.state';

import { AiPromptResponse, Colors, ConciergeActions, LoadingStates } from 'typings';

import OnOffScript from './OnOffScript';
import AiAssistant from './AIAssistant';

type Props = {
  className?: string;
};

interface ButtonAttributes {
  $backgroundcolor?: string;
  $color?: string;
}

interface TextFieldAttributes {
  $bordercolor?: string;
  $isOverCharacterLimit?: boolean;
}

const StyledTextField: any = styled(TextField)<TextFieldAttributes>`
  && {
    width: 100%;
    box-shadow: -3px 3px 6px ${Colors.TRANSPARENT_BLACK};
    flex-direction: unset;

    & .MuiInputBase-root {
      align-items: start;
      padding-top: 8px;
    }

    & .MuiInput-underline {
      width: 100%;
      &:hover:before,
      &:after {
        border-bottom: 2px solid ${(props) => props.$bordercolor} !important;
      }
    }

    & .MuiFormHelperText-root {
      position: absolute;
      bottom: 0;
      right: 0;
      margin: ${(props) => props.theme.spacing(0, 1)};
      color: ${(props) => (props.$isOverCharacterLimit ? 'tomato' : 'inherit')};
    }
  }
`;

const StyledToggleButton = styled(ToggleButton)<ButtonAttributes>`
  && {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    flex-grow: 1;
    padding: 0;

    &.Mui-disabled {
      pointer-events: auto;
    }

    &.Mui-selected {
      background-color: ${(props) => props.$backgroundcolor};
      color: white;
    }

    .MuiToggleButton-label {
      display: block;
    }
  }
`;

const StyledToggleButtonLabel = styled(Box)`
  && {
    padding: 11px;
  }
`;

const StyledButton = styled(Button)<ButtonAttributes>`
  && {
    color: ${(props) => props.$color || Colors.WHITE};
    background-color: ${(props) => props.$backgroundcolor};
  }
`;

const StyledRadio: any = styled(Radio)<ButtonAttributes>`
  &.Mui-checked {
    color: ${(props) => props.$color};
  }
`;

const MessageEntry = ({ className }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const lead = useSelector((state: RootState) => ({
    aiPromptResponse: state.lead.aiPromptResponse,
    defaultCommType: state.lead.defaultCommType,
    entity: state.lead.entity,
    isLoaded: state.lead.communicationLoadingState === LoadingStates.LOADED,
    messageTemplates: state.lead.messageTemplates,
  }));
  const isSmsCapable = useSelector(selectLeadSmsCapable);
  const isEmailCapable = useSelector(selectLeadEmailCapable);
  const concierge = useSelector(
    (state: RootState) => ({
      id: state.concierge.entity?.id,
      name: state.concierge.entity?.name,
      isEngagedWithSystemPresence: state.concierge.isEngagedWithSystemPresence,
    }),
    shallowEqual
  );
  const script = useSelector((state: RootState) => ({
    currentStepName: state.script.currentStepName,
    onScriptStep: state.script.onScriptStep,
  }));
  const { trackEvent } = useTracking();

  // determine the default action based on the lead's capabilities and last used action
  let defaultAction: ConciergeActions | null = null;

  if (lead.isLoaded) {
    if (isSmsCapable && (!lead.defaultCommType || lead.defaultCommType === ConciergeActions.SMS)) {
      defaultAction = ConciergeActions.SMS;
    } else if (isEmailCapable) {
      defaultAction = ConciergeActions.EMAIL;
    } else {
      defaultAction = ConciergeActions.NOTE;
    }
  }

  const messagePlaceholder = lead.entity?.leadType
    ? `Enter your message for this "${lead.entity?.leadType}" lead`
    : 'Enter your message';

  const [action, setAction] = useState(defaultAction?.toString() ?? '');
  const [noteType, setNoteType] = useState('admin');
  const [characterCount, setCharacterCount] = useState(0);
  const latestAiPromptResponse = useRef<AiPromptResponse | null>(null);

  // set color based on the action
  let color = theme.palette.primary.main;

  switch (action) {
    case ConciergeActions.SMS:
      color = theme.palette.secondary.main;
      break;
    case ConciergeActions.EMAIL:
      color = Colors.DARK_PURPLE;
      break;
  }

  const { handleSubmit, control, setValue, register, reset, getValues } = useForm({
    defaultValues: {
      message: '',
      action,
    },
  });

  const handleCharacterCountChange = useCallback(() => {
    const message = getValues('message');
    const characterCount = message?.length ?? 0;

    setCharacterCount(characterCount);
  }, [getValues]);

  useEffect(() => {
    register({ name: 'action' });
  }, [register]);

  useEffect(() => {
    function scriptCopyHandler(evt: CustomEventInit) {
      const { action: currentAction } = getValues();
      if (currentAction !== ConciergeActions.NOTE && evt?.detail?.message !== undefined) {
        setValue('message', evt.detail.message);
        handleCharacterCountChange();
      }
    }

    document.addEventListener('scriptCopy', scriptCopyHandler);

    return () => {
      document.removeEventListener('scriptCopy', scriptCopyHandler);
    };
  }, [getValues, setValue, handleCharacterCountChange]);

  // add AI prompt response to message box
  useEffect(() => {
    // only run if a new AI prompt response has been generated
    if (lead.aiPromptResponse && lead.aiPromptResponse.id !== latestAiPromptResponse.current?.id) {
      setValue('message', lead.aiPromptResponse.content);
      handleCharacterCountChange();

      // set script toggle appropriately based on the prompt that was run
      if (lead.aiPromptResponse.isOnScript) {
        dispatch(setIsOnScript({ isOnScript: true, onScriptStep: script.currentStepName }));
      } else {
        dispatch(setIsOnScript({ isOnScript: false, onScriptStep: null }));
      }

      // store latest AI prompt response
      latestAiPromptResponse.current = lead.aiPromptResponse;
    }
  }, [
    dispatch,
    setValue,
    lead.aiPromptResponse,
    latestAiPromptResponse,
    script.currentStepName,
    handleCharacterCountChange,
  ]);

  useEffect(() => {
    // fail safe to clear AP message after certain amount of time
    if (concierge.isEngagedWithSystemPresence) {
      const timeoutHandler = setTimeout(() => {
        dispatch(setSystemPresenceEngagement(false));
      }, SystemPresenceTimeout);

      return () => {
        clearTimeout(timeoutHandler);
      };
    }

    return undefined;
  }, [dispatch, concierge.isEngagedWithSystemPresence]);

  useEffect(() => {
    // set the action to the default action if it is not set
    if (defaultAction && !action) {
      setAction(defaultAction.toString() ?? '');
    }
  }, [defaultAction, action]);

  useEffect(() => {
    // set the action value in the form
    if (action) {
      setValue('action', action);
    }
  }, [action, setValue]);

  let actionMessage;
  switch (action) {
    case ConciergeActions.SMS:
      actionMessage = 'Send sms';
      break;
    case ConciergeActions.EMAIL:
      actionMessage = 'Send email';
      break;
    case ConciergeActions.NOTE:
      actionMessage = 'Add note';
      break;
    default:
      break;
  }

  const handleNoteChange = (event: any) => {
    setNoteType(event.target.value);
  };

  const handleUserKeyDown = (e: any) => {
    // submit message form when user presses enter key
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const onSubmit = (data: any) => {
    if (!data.message) return;

    reset();
    handleCharacterCountChange();
    switch (action) {
      case ConciergeActions.SMS:
        data = {
          text: data.message,
          leadId: lead.entity?.id,
          currentStep: script.onScriptStep,
          aiPromptResponseId: lead.aiPromptResponse?.id,
        };
        dispatch(sendMessageToLead({ data, type: action }));
        // reset on-script state
        dispatch(setIsOnScript({ isOnScript: false, onScriptStep: null }));
        // reset AI prompt response state
        dispatch(clearAiPromptResponse());
        trackEvent({
          event: 'event',
          eventProps: {
            category: 'Communication',
            action: 'Sent SMS to Lead',
          },
        });
        break;
      case ConciergeActions.EMAIL:
        data = {
          leadId: lead.entity?.id,
          text: data.message,
        };
        dispatch(sendMessageToLead({ data, type: action }));
        break;
      case ConciergeActions.NOTE:
        dispatch(apiCreateNote({ type: noteType, note: data.message }));
        break;
      default:
        break;
    }
  };

  const buildQuickRepliesMenu = () => {
    const menuItems = lead.messageTemplates.map((messageTemplate) => ({
      text: messageTemplate.friendlyName,
      icon: <FormatQuote />,
      action: messageTemplate.randomVariant?.message || '',
    }));

    return menuItems;
  };

  const handleQuickRepliesMenuSelection = (menuItem: any) => {
    copyToClipboard(menuItem.action).then(
      () => dispatch(setNotification({ type: 'success', text: 'Text copied' })),
      () => dispatch(setNotification({ type: 'error', text: 'Could not copy text' }))
    );
  };

  const showQuickRepliesMenu = (): boolean => {
    return !!lead.messageTemplates?.length;
  };

  return (
    <Stack className={className}>
      {concierge.isEngagedWithSystemPresence && (
        <span className="msg-system-presence">Alex Parker is thinking...</span>
      )}
      <form className="frm-message-entry" onSubmit={handleSubmit(onSubmit)}>
        <ToggleButtonGroup
          value={action}
          onChange={(event, newAction) => newAction && setAction(newAction)}
          exclusive
          orientation="vertical"
          aria-label="message action"
        >
          <StyledToggleButton
            value={ConciergeActions.SMS}
            $backgroundcolor={color}
            disabled={!isSmsCapable}
          >
            <ContextMenu
              menuItems={buildQuickRepliesMenu()}
              onSelect={handleQuickRepliesMenuSelection}
              enabled={showQuickRepliesMenu() && isSmsCapable}
              cursor={'pointer'}
            >
              <Tooltip
                title={!isSmsCapable ? 'Lead cannot receive SMS' : ''}
                arrow={true}
                placement={'top'}
              >
                <StyledToggleButtonLabel>sms</StyledToggleButtonLabel>
              </Tooltip>
            </ContextMenu>
          </StyledToggleButton>
          <StyledToggleButton
            value={ConciergeActions.EMAIL}
            $backgroundcolor={color}
            disabled={!isEmailCapable}
          >
            <ContextMenu
              menuItems={buildQuickRepliesMenu()}
              onSelect={handleQuickRepliesMenuSelection}
              enabled={showQuickRepliesMenu() && isEmailCapable}
              cursor={'pointer'}
            >
              <Tooltip
                title={!isEmailCapable ? 'Lead cannot receive email' : ''}
                arrow={true}
                placement={'top'}
              >
                <StyledToggleButtonLabel>email</StyledToggleButtonLabel>
              </Tooltip>
            </ContextMenu>
          </StyledToggleButton>
          <StyledToggleButton value={ConciergeActions.NOTE} $backgroundcolor={color}>
            <StyledToggleButtonLabel>notes</StyledToggleButtonLabel>
          </StyledToggleButton>
        </ToggleButtonGroup>
        <Box className="message-section">
          <Controller
            as={
              <StyledTextField
                multiline
                placeholder={messagePlaceholder}
                $bordercolor={color}
                rows={6}
                onKeyDown={handleUserKeyDown}
                onKeyUp={handleCharacterCountChange}
                helperText={`${characterCount}/${SmsCharacterLimit}`}
                $isOverCharacterLimit={characterCount > SmsCharacterLimit}
              />
            }
            name="message"
            control={control}
          />
          {action === ConciergeActions.SMS && <AiAssistant />}
        </Box>
        <Box className="action-section">
          {actionMessage && (
            <StyledButton $backgroundcolor={color} type="submit">
              {actionMessage}
            </StyledButton>
          )}
          {action === ConciergeActions.NOTE && (
            <RadioGroup
              defaultValue="admin"
              value={noteType}
              onChange={handleNoteChange}
              row
              className="radio-group"
            >
              <FormControlLabel
                value="admin"
                control={<StyledRadio $color={color} />}
                label="Admin"
              />
              <FormControlLabel
                value="client"
                control={<StyledRadio $color={color} />}
                label="Client"
              />
            </RadioGroup>
          )}
          {action === ConciergeActions.SMS && <OnOffScript />}
        </Box>
      </form>
    </Stack>
  );
};

const StyledMessageEntry = styled(MessageEntry)`
  .frm-message-entry {
    padding: 0;
    display: flex;
    flex-direction: row;
    background: ${(props) => props.theme.palette.background.default};
    border-radius: ${(props) => props.theme.spacing(1)};

    & .MuiToggleButtonGroup-vertical {
      width: 20%;
    }

    .message-section {
      display: flex;
      flex-direction: row;
      position: relative;
      width: 60%;

      .ai-assistant {
        align-items: center;
        bottom: 0;
        cursor: pointer;
        display: flex;
        font-size: 0.75rem;
        left: 0;
        line-height: 1.66;
        margin: 0 0.5rem;
        position: absolute;
        user-select: none;

        svg {
          font-size: 1rem;
          margin: 0 2px 0 -1px;
        }

        .spin {
          animation: spin 2s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(-360deg);
          }
        }
      }
    }

    .action-section {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      align-items: center;
      width: 20%;

      .radio-group {
        justify-content: center;
      }
    }
  }

  .msg-system-presence {
    text-align: right;
    color: ${(props) => props.theme.palette.text.primary};
  }
`;

export default StyledMessageEntry;
